import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../services/api'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { 
  Users, 
  Search, 
  Filter, 
  Download, 
  Unlock, 
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Mail,
  Upload,
  Eye
} from 'lucide-react'

function AdminDashboard() {
  const { companySlug } = useParams()
  const { user } = useAuth()
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [newEmployeeEmail, setNewEmployeeEmail] = useState('')
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const canManageEmployees = user?.role === 'hr_admin'

  useEffect(() => {
    fetchEmployees()
  }, [companySlug, filter])

  const fetchEmployees = async () => {
    try {
      setLoading(true)
      const params = filter !== 'all' ? { status: filter } : {}
      const response = await api.get(`/${companySlug}/admin/employees`, { params })
      setEmployees(response.data)
    } catch (error) {
      toast.error('Failed to load employees')
    } finally {
      setLoading(false)
    }
  }

  const handleAddEmployee = async (e) => {
    e.preventDefault()
    try {
      await api.post(`/${companySlug}/admin/employees`, {
        email: newEmployeeEmail,
        role: 'employee'
      })
      toast.success('Employee added and invitation sent')
      setShowAddModal(false)
      setNewEmployeeEmail('')
      fetchEmployees()
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add employee'
      toast.error(message)
    }
  }

  const handleBulkInvite = async (e) => {
    e.preventDefault()
    const file = e.target.csvFile.files[0]
    if (!file) {
      toast.error('Please select a CSV file')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await api.post(`/${companySlug}/admin/employees/bulk-invite`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      toast.success(`Invited ${response.data.invited_count} employees`)
      setShowBulkModal(false)
      fetchEmployees()
    } catch (error) {
      toast.error('Failed to process bulk invite')
    }
  }

  const handleUnlock = async (employeeId) => {
    try {
      await api.post(`/${companySlug}/admin/employees/${employeeId}/unlock`)
      toast.success('Form unlocked successfully')
      fetchEmployees()
    } catch (error) {
      toast.error('Failed to unlock form')
    }
  }

  const handleExportPDF = async (employeeId, name) => {
    try {
      const response = await api.get(`/${companySlug}/exports/personal-info/${employeeId}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Personal_Info_${name}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('PDF downloaded')
    } catch (error) {
      toast.error('Failed to export PDF')
    }
  }

  const handleExportTemplate = async (type) => {
    try {
      const endpoint = type === 'paysquare' 
        ? `/${companySlug}/exports/all-employees/paysquare-template`
        : `/${companySlug}/exports/all-employees/employee-template`
      
      const response = await api.get(endpoint, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Export downloaded')
    } catch (error) {
      toast.error('Failed to export')
    }
  }

  const getStatusBadge = (employee) => {
    if (employee.is_submitted) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" /> Submitted
        </span>
      )
    } else if (employee.last_saved_at) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" /> In Progress
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <AlertCircle className="h-3 w-3 mr-1" /> Not Started
        </span>
      )
    }
  }

  const filteredEmployees = employees.filter(emp => 
    emp.email.toLowerCase().includes(search.toLowerCase()) ||
    emp.full_name?.toLowerCase().includes(search.toLowerCase())
  )

  const stats = {
    total: employees.length,
    submitted: employees.filter(e => e.is_submitted).length,
    inProgress: employees.filter(e => !e.is_submitted && e.last_saved_at).length,
    notStarted: employees.filter(e => !e.is_submitted && !e.last_saved_at).length
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR Admin Dashboard</h1>
          <p className="text-gray-600">Manage employee onboarding</p>
        </div>
        {canManageEmployees && (
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkModal(true)}
              className="btn-secondary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" /> Bulk Invite
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Employee
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-primary-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Employees</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="text-2xl font-bold">{stats.submitted}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold">{stats.inProgress}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Not Started</p>
              <p className="text-2xl font-bold">{stats.notStarted}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      {canManageEmployees && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Exports</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleExportTemplate('employee')}
              className="btn-secondary flex items-center"
            >
              <FileText className="h-4 w-4 mr-2" /> Employee Template (XLSX)
            </button>
            <button
              onClick={() => handleExportTemplate('paysquare')}
              className="btn-secondary flex items-center"
            >
              <FileText className="h-4 w-4 mr-2" /> PaySquare Template (XLSX)
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="all">All Employees</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
            </select>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full md:w-80"
            />
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Activity
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </td>
              </tr>
            ) : filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  No employees found
                </td>
              </tr>
            ) : (
              filteredEmployees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {employee.full_name || 'Not provided'}
                      </p>
                      <p className="text-sm text-gray-500">{employee.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(employee)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {employee.last_saved_at 
                      ? new Date(employee.last_saved_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <Link
                      to={`/${companySlug}/admin/review/${employee.id}`}
                      className="text-gray-700 hover:text-gray-900 inline-flex items-center"
                      title="Review details"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    {employee.is_submitted && (
                      <>
                        {canManageEmployees && (
                          <button
                            onClick={() => handleUnlock(employee.id)}
                            className="text-amber-600 hover:text-amber-800"
                            title="Unlock for editing"
                          >
                            <Unlock className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleExportPDF(employee.id, employee.full_name || employee.email)}
                          className="text-primary-600 hover:text-primary-800"
                          title="Download profile PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Employee</h2>
            <form onSubmit={handleAddEmployee}>
              <div className="mb-4">
                <label className="label">Email Address</label>
                <input
                  type="email"
                  required
                  value={newEmployeeEmail}
                  onChange={(e) => setNewEmployeeEmail(e.target.value)}
                  className="input"
                  placeholder="employee@company.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a company domain email. An invitation will be sent automatically.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex items-center">
                  <Mail className="h-4 w-4 mr-2" /> Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Invite Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Bulk Invite Employees</h2>
            <form onSubmit={handleBulkInvite}>
              <div className="mb-4">
                <label className="label">Upload CSV File</label>
                <input
                  type="file"
                  name="csvFile"
                  accept=".csv"
                  required
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                <p className="text-xs text-gray-500 mt-2">
                  CSV format: email (required), role (optional, default: employee)
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex items-center">
                  <Upload className="h-4 w-4 mr-2" /> Process Invites
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
